import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SERIES } from './chart-theme';
import { Sparkline } from './sparkline';

export interface KpiItem {
  label: string;
  value: string;
  /** Signed change vs the prior period, pre-formatted (e.g. "+1.4"). */
  delta: string;
  /** Direction of travel only. */
  up: boolean;
  trend: number[];
}

/**
 * Headline figures above the grid.
 *
 * Deliberately outside the draggable grid: these are the dashboard's fixed
 * frame of reference, and a KPI the reader has to hunt for isn't a KPI.
 *
 * The arrow and colour encode **direction, not judgement** — more internal
 * hours isn't inherently bad, and this dashboard sets no targets. Colour says
 * "up" or "down"; the reader decides what that means.
 */
export function KpiStrip({ items }: { items: KpiItem[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {items.map((k) => {
        const Arrow = k.up ? TrendingUp : TrendingDown;
        const color = k.up ? SERIES.positive : SERIES.negative;
        return (
          <Card key={k.label} className="gap-1 px-3 py-2.5">
            {/* Tracked uppercase micro-label over a tight, heavy figure. The
                label recedes to a caption and the number carries the tile —
                at equal weight the two compete and the strip reads as noise. */}
            <p
              className="truncate font-semibold text-[9.5px] text-muted-foreground uppercase tracking-[0.07em]"
              title={k.label}
            >
              {k.label}
            </p>
            <p className="font-extrabold text-2xl tabular-nums tracking-[-0.02em]">{k.value}</p>
            <p className="flex items-center gap-1 text-[11px]">
              <Arrow className="size-3" style={{ color }} aria-hidden />
              <span className="tabular-nums" style={{ color }}>
                {k.delta}
              </span>
              <span className="text-muted-foreground">vs prior mo</span>
            </p>
            <Sparkline data={k.trend} color={color} />
          </Card>
        );
      })}
    </div>
  );
}
